// Host lifecycle adapter — the ONLY place supplier/host integration touches MWQ.
// Contract: launch · suspend · resume · audioInterrupt · navigateAway · reset · shutdown.
// The core app listens to `mwq:host` window events; a supplier adapter translates its own API into these calls.
// No proprietary APIs are assumed here — `testAdapter` drives the same contract from the keyboard / test scripts.
import { setMutedTemporarily } from "@/lib/soundCues";

const TOUCHED_KEY = "mwq_air_touched_keys";
const EVT = "mwq:host";
let tracking = false;

function emit(type, detail) { try { window.dispatchEvent(new CustomEvent(EVT, { detail: { type, ...detail } })); } catch { /* ignore */ } }
export function onHostEvent(fn) { const h = (e) => fn(e.detail); window.addEventListener(EVT, h); return () => window.removeEventListener(EVT, h); }

/** Record every localStorage key written during a shared session so Reset can clear passenger state only. */
export function startSessionTracking() {
  if (tracking) return;
  tracking = true;
  try {
    const ls = window.localStorage;
    const isNative = typeof Storage !== "undefined" && Object.getPrototypeOf(ls) === Storage.prototype;
    const target = isNative ? Storage.prototype : ls; // never patch Object.prototype (in-memory fallback)
    const orig = target.setItem;
    target.setItem = function (k, v) {
      if ((isNative ? this === window.localStorage : true) && k !== TOUCHED_KEY && k !== "mwq_runtime_profile") {
        try { const set = new Set(JSON.parse(window.localStorage.getItem(TOUCHED_KEY) || "[]")); if (!set.has(k)) { set.add(k); orig.call(window.localStorage, TOUCHED_KEY, JSON.stringify([...set])); } } catch { /* ignore */ }
      }
      return orig.call(this, k, v);
    };
  } catch { /* storage unavailable: nothing to track */ }
}

export const host = {
  launch(detail = {}) { emit("launch", detail); },
  suspend() { setMutedTemporarily(true); emit("suspend", {}); },
  resume() { setMutedTemporarily(false); emit("resume", {}); },
  audioInterrupt(active) { setMutedTemporarily(!!active); emit("audioInterrupt", { active: !!active }); },
  navigateAway() { setMutedTemporarily(true); emit("navigateAway", {}); },
  /** Clears passenger state (keys written this session + sessionStorage), never app assets/caches. */
  reset() {
    let cleared = 0;
    try {
      const keys = JSON.parse(localStorage.getItem(TOUCHED_KEY) || "[]");
      for (const k of keys) { localStorage.removeItem(k); cleared++; }
      localStorage.removeItem(TOUCHED_KEY);
    } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    emit("reset", { cleared });
    return cleared;
  },
  shutdown() { setMutedTemporarily(true); emit("shutdown", {}); },
};

/** Test adapter: Alt+Shift+S suspend · R resume · X reset · A audio interrupt toggle · N navigate-away. Also `window.__mwqHost`. */
export function installTestAdapter() {
  window.__mwqHost = host;
  const onKey = (e) => {
    if (!(e.altKey && e.shiftKey)) return;
    const k = e.key.toLowerCase();
    if (k === "s") host.suspend(); else if (k === "r") host.resume(); else if (k === "x") host.reset(); else if (k === "a") host.audioInterrupt(!window.__mwqAudioInt), (window.__mwqAudioInt = !window.__mwqAudioInt); else if (k === "n") host.navigateAway();
  };
  window.addEventListener("keydown", onKey);
  document.addEventListener("visibilitychange", () => (document.hidden ? host.suspend() : host.resume()));
  return () => window.removeEventListener("keydown", onKey);
}
